/**
 * The small arrow in a column header, and the menu it opens — what a business
 * application puts on every column: a place to filter the column, without a
 * toolbar of boxes above the table.
 *
 * The menu's content is the caller's: this component knows how to open, close
 * and say so. It closes on Escape and on a click anywhere else, and the button
 * says it has a popup and whether it is open (`aria-haspopup`, `aria-expanded`),
 * so it is not only drawn.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import "./data.css";

export interface ColumnMenuProps {
  /** Spoken on the arrow: "Filter Designation". Translated by the caller. */
  label: string;
  /** A filter is on for this column: the arrow is filled, so it can be seen. */
  active?: boolean;
  children: ReactNode;
}

export function ColumnMenu({ label, active = false, children }: ColumnMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Opening puts the cursor in the first field of the menu, so typing can start.
  useEffect(() => {
    if (open) panel.current?.querySelector<HTMLElement>("input, button:not([disabled])")?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <span ref={root} className="hardauto-colmenu" data-active={active ? "true" : undefined}>
      <button
        type="button"
        className="hardauto-colmenu__button"
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {active ? "▼" : "▾"}
      </button>
      {open ? (
        <div
          ref={panel}
          id={panelId}
          role="group"
          aria-label={label}
          className="hardauto-colmenu__panel"
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}
